import { useEffect, useState } from 'react';
import { EmptyState, ErrorState, Skeleton } from '@store/ui';
import { adminApi, ApiError, type SessionResponse } from '../../api';
import { AdminCategoriesScreen, AdminCategoryFormScreen } from './categories';
import { AdminProductFormScreen } from './product-form';
import { AdminProductsScreen } from './products';
import { AdminStockScreen } from './stock';

/**
 * Управление магазином.
 *
 * One entry into the whole section, so `app.tsx` gains a single case and the tab
 * bar gains nothing: this is reached from the Профиль screen and from nowhere
 * else, which is what the four-tab bar means.
 */
export type AdminRoute =
  | { readonly kind: 'products' }
  | { readonly kind: 'product'; readonly productId: string | null }
  | { readonly kind: 'stock'; readonly productId: string }
  | { readonly kind: 'categories' }
  | { readonly kind: 'category'; readonly categoryId: string | null };

/**
 * Reads the hash below `/admin`. Returns null for anything it does not know, so
 * the router can fall through rather than render an empty management screen.
 */
export function resolveAdminRoute(path: string): AdminRoute | null {
  if (path === '/admin' || path === '/admin/products') return { kind: 'products' };
  if (path === '/admin/products/new') return { kind: 'product', productId: null };
  if (path === '/admin/categories') return { kind: 'categories' };
  if (path === '/admin/categories/new') return { kind: 'category', categoryId: null };

  const stock = /^\/admin\/products\/([^/]+)\/stock$/.exec(path);
  if (stock) return { kind: 'stock', productId: decodeURIComponent(stock[1]!) };

  const product = /^\/admin\/products\/([^/]+)$/.exec(path);
  if (product) return { kind: 'product', productId: decodeURIComponent(product[1]!) };

  const category = /^\/admin\/categories\/([^/]+)$/.exec(path);
  if (category) return { kind: 'category', categoryId: decodeURIComponent(category[1]!) };

  return null;
}

export function AdminSection({
  route,
  session,
  onNavigate,
}: {
  readonly route: AdminRoute;
  readonly session: SessionResponse | null;
  readonly onNavigate: (route: string) => void;
}) {
  /*
   * A second sign-in, and the section cannot work without it.
   *
   * The API resolves a session under the scope the URL implies — `app.ts` reads
   * `/api/admin` as `admin` and everything else as `miniapp` — and
   * `resolveSession` matches that scope exactly. The customer's token is
   * therefore not merely insufficient under `/api/admin`, it is invisible: the
   * server finds no session at all and answers before any role is read. Handing
   * the customer session to the admin transport, which is what this screen used
   * to do, produced «Недостаточно прав» on every request in the section.
   *
   * `signIn` mints an admin-scoped session from the same signed `initData` that
   * Telegram issued at launch. It has to happen before the first screen renders,
   * because each of them fetches on mount.
   */
  const [auth, setAuth] = useState<'signing-in' | 'ready' | { error: string }>('signing-in');

  const canOpen = session?.permissions.includes('catalog.write') ?? false;

  useEffect(() => {
    if (!canOpen) return;
    let cancelled = false;

    adminApi
      .signIn()
      .then(() => {
        if (!cancelled) setAuth('ready');
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        /*
         * `initData` is minted once at launch and the server refuses it after
         * fifteen minutes, so an owner who has had the shop open a while gets a
         * real refusal here rather than a bug. Say which it is.
         */
        setAuth({
          error:
            cause instanceof ApiError
              ? cause.message
              : 'Не удалось войти в управление. Закройте и откройте магазин заново.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [canOpen]);

  /*
   * A refusal on the client is a courtesy, not a gate: every one of these routes
   * is checked again by `requirePermission(request, 'catalog.write')` on the
   * server, which reads the role out of the database. This only keeps somebody
   * who pasted a URL from meeting a wall of 403s instead of a sentence.
   */
  if (!canOpen) {
    return (
      <div className="container">
        <EmptyState
          icon="alert"
          title="Раздел недоступен"
          text="Управление магазином открыто только владельцу и администраторам."
        />
      </div>
    );
  }

  if (typeof auth === 'object') {
    return (
      <div className="container">
        <ErrorState
          title="Управление недоступно"
          text={auth.error}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  if (auth === 'signing-in') {
    // Shaped like the list behind it, so the first paint does not jump.
    return (
      <div className="container stack">
        <Skeleton height={28} width="52%" />
        <Skeleton height={168} />
      </div>
    );
  }

  switch (route.kind) {
    case 'product':
      return <AdminProductFormScreen productId={route.productId} onNavigate={onNavigate} />;
    case 'stock':
      return <AdminStockScreen productId={route.productId} onNavigate={onNavigate} />;
    case 'categories':
      return <AdminCategoriesScreen onNavigate={onNavigate} />;
    case 'category':
      return <AdminCategoryFormScreen categoryId={route.categoryId} onNavigate={onNavigate} />;
    default:
      return <AdminProductsScreen onNavigate={onNavigate} />;
  }
}
